// @ts-nocheck
import { format } from "date-fns";
import ApiError from "./api-error";

export const flattenObject = (obj, delimiter = ".", prefix = "") =>
  Object.keys(obj).reduce((acc, k) => {
    const pre = prefix.length ? `${prefix}${delimiter}` : "";
    if (
      typeof obj[k] === "object" &&
      obj[k] !== null &&
      Object.keys(obj[k]).length > 0
    )
      Object.assign(acc, flattenObject(obj[k], delimiter, pre + k));
    else acc[k] = obj[k];
    return acc;
  }, {});

export const percentage = (
  basePrice: number,
  targetPrice: number,
  negative = true,
) => {
  const percentage = (targetPrice / basePrice) * 100;
  const absolute = negative ? percentage - 100 : 100 - percentage;
  return `${Math.round(absolute)}%`;
};

export type Formatter<T = string> = {
  format: (value: T) => string;
  parse: (value: string) => T;
};

export const currencyFormatter: (config?: {
  locale?: string;
  currency?: string;
}) => Formatter<number> = ({ locale = "it-IT", currency = "EUR" } = {}) => {
  const numberFormatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    useGrouping: true,
  });

  return {
    format: (value: number) => {
      if (value == null || Number.isNaN(value)) {
        return "";
      }
      return numberFormatter.format(value);
    },
    parse: (value: string) => {
      // console.log('value', value)
      const rawValue = parseInt(value.replace(/\D/g, ""), 10) || 0;
      // console.log('raw', rawValue)

      return rawValue / 100;
    },
  };
};

export const truncate = (input: string, length = 30) =>
  input?.length > length ? `${input.substring(0, length)}...` : input;

const safeDecode = (v: string) => {
  try {
    return decodeURIComponent(v);
  } catch {
    return v;
  }
};

export const objToQueryParams = (obj: any) => {
  const queryParams = new URLSearchParams();

  if (obj) {
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined && value !== null && value !== "") {
        if (value.constructor !== Array || value.length > 0) {
          if (Array.isArray(value)) {
            value.forEach((val) => {
              queryParams.append(key, val);
            });
          } else {
            queryParams.append(
              key,
              typeof value === "string" ? safeDecode(value) : (value as any),
            );
          }
        }
      }
    }
  }

  return queryParams.toString();
};

export const queryParamsToObj = (queryParams: string): Record<string, any> => {
  const obj: Record<string, any> = {};

  if (queryParams) {
    const cleanedParams = queryParams.startsWith("?")
      ? queryParams.slice(1)
      : queryParams;

    const params = new URLSearchParams(cleanedParams);

    for (const [key, value] of params.entries()) {
      if (obj.hasOwnProperty(key)) {
        if (Array.isArray(obj[key])) {
          obj[key].push(value);
        } else {
          obj[key] = [obj[key], value];
        }
      } else {
        obj[key] = value;
      }
    }
  }

  return obj;
};

export async function generateSHA1(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest("SHA-1", data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export function removeEmptyValues(obj: any): any {
  const result: any = {};
  for (const [key, value] of Object.entries(obj)) {
    if (
      value !== null &&
      value !== undefined &&
      !(typeof value === "string" && value.trim() === "") &&
      !(Array.isArray(value) && value.length === 0)
    ) {
      result[key] = value;
    }
  }
  return result;
}

export function objToArrayKeys(obj: {
  [key: string]: string | string[] | number | null;
}) {
  const keys = [];

  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined && value !== null && value !== "") {
      if (Array.isArray(value)) {
        value.forEach((item) => {
          keys.push(String(key + item) + "array");
        });
      } else {
        keys.push(String(key + value));
      }
    }
  }

  return keys;
}

// TODO: localize this with global locale
export const formatDate = (
  date: Date | undefined,
  _format: string = "dd-MM-yyyy",
) => (date ? format(date, _format) : undefined);

export function sanitizeObj(obj: any, keys: string[]): any {
  const result: any = {};
  for (const [key, value] of Object.entries(obj)) {
    if (
      !keys.includes(key) &&
      value !== "" &&
      value !== null &&
      value !== undefined
    ) {
      result[key] = value;
    }
  }
  return result;
}

export function downloadCSV(path: string) {
  window.location.href = `/api/download-csv?path=${encodeURIComponent(path)}`;
}

export function transformNullToEmpty<T>(data: T): T {
  if (data === null) {
    return "" as unknown as T;
  }
  if (Array.isArray(data)) {
    return data.map((item) => transformNullToEmpty(item)) as unknown as T;
  }
  if (typeof data === "object") {
    return Object.entries(data).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: transformNullToEmpty(value),
      }),
      {} as T,
    );
  }

  return data;
}

export function getApiLocalizedProp(
  prop: { lang: { [key: string]: string } },
  locale: string,
) {
  if (prop?.lang[locale.split("-")[0]]) {
    return prop.lang[locale.split("-")[0]];
  }

  return "Missing api field";
}

export async function enrichQueryFn(fn: any) {
  const res = await fn();
  if (res._nextjs_error) throw new ApiError(res._nextjs_error);
  return res;
}

export const sanifyProductTitle = (title: string) =>
  title.replace(/[^a-zA-Z0-9]/g, "-");

export function sizesSort(arr) {
  const sizes = [
    "XXS",
    "2XS",
    "XS",
    "XS/S",
    "S",
    "S/M",
    "M",
    "M/L",
    "L",
    "L/XL",
    "XL",
    "XXL",
    "2XL",
    "XXXL",
    "2XL",
    "3XL",
    "4XL",
    "5XL",
    "6XL",
    "7XL",
    "8XL",
  ];
  const sizeRank = Object.fromEntries(sizes.map((s, i) => [s, i]));

  // Numbers
  if (arr.every((v) => typeof v === "number")) {
    return [...arr].sort((a, b) => a - b);
  }

  // Sizes
  if (arr.every((v) => sizeRank[v] !== undefined)) {
    return [...arr].sort((a, b) => sizeRank[a] - sizeRank[b]);
  }

  // Alphabetic
  if (arr.every((v) => typeof v === "string")) {
    // Strings as numbers
    if (arr.every((v) => !isNaN(parseFloat(v.replace(",", "."))))) {
      return [...arr].sort(
        (a, b) =>
          parseFloat(a.replace(",", ".")) - parseFloat(b.replace(",", ".")),
      );
    }

    return [...arr].sort((a, b) => a.localeCompare(b));
  }

  // Numbers/string mix
  return [...arr].sort((a, b) => {
    if (typeof a === "number" && typeof b === "number") return a - b;
    if (typeof a === "string" && typeof b === "string")
      return a.localeCompare(b);
    return typeof a === "number" ? -1 : 1;
  });
}

export function flattenTree(nodes) {
  const out = [];

  function visit(node) {
    const { children, ...rest } = node;
    out.push(rest);

    if (Array.isArray(children) && children.length) {
      for (const child of children) visit(child);
    }
  }

  for (const n of nodes) visit(n);
  return out;
}

export function unique(values) {
  return [...new Set(values)];
}
